import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Check, X } from 'lucide-react';

import { Spinner } from 'kmstack-shared/components/basic';
import { HTTPStatus, logger } from 'kmstack-shared/core';
import { useAlert } from 'kmstack-shared/hooks';
import { Input, Label, ActionButton } from '@/components';
import { ETitle } from '@/types/types';
import { AuthLayout } from './AuthLayout';
import { AuthService } from '../services/auth.service';
import { useAuthRedirect } from '../hooks/auth.useAuthRedirect';
import { input_style } from '@/utils/style';

// Step 1: Email submission
const emailSchema = z.object({
    email: z.string().email('Please enter a valid email address'),
});

// Step 3: New password
const passwordSchema = z.object({
    newPassword: z.string()
        .min(8, 'Password must be at least 8 characters')
        .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
        .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
        .regex(/[0-9]/, 'Password must contain at least one number')
        .regex(/[^a-zA-Z0-9]/, 'Password must contain at least one special character'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
}).refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
});

type EmailFormData = z.infer<typeof emailSchema>;
type PasswordFormData = z.infer<typeof passwordSchema>;

const ForgotPassword: React.FC = () => {
    const { notify } = useAlert();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [email, setEmail] = useState('');
    const [resetToken, setResetToken] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isValidatingToken, setIsValidatingToken] = useState(false);
    const [tokenError, setTokenError] = useState<string | null>(null);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Rate limit state
    const [rateLimitUntil, setRateLimitUntil] = useState<number | null>(null);
    const [timeRemaining, setTimeRemaining] = useState<number>(0);

    // Check auth status (redirects if already logged in)
    const { loading: authLoading } = useAuthRedirect({});

    // Check for existing rate limit on mount
    useEffect(() => {
        const storedRateLimit = localStorage.getItem('password_reset_rate_limit');
        if (storedRateLimit) {
            const limitTimestamp = parseInt(storedRateLimit);
            if (Date.now() < limitTimestamp) {
                setRateLimitUntil(limitTimestamp);
            } else {
                // Expired, clear it
                localStorage.removeItem('password_reset_rate_limit');
            }
        }
    }, []);

    // Update countdown timer every second
    useEffect(() => {
        if (!rateLimitUntil) {
            setTimeRemaining(0);
            return;
        }

        const updateTimer = () => {
            const remaining = Math.max(0, rateLimitUntil - Date.now());
            setTimeRemaining(remaining);

            if (remaining === 0) {
                setRateLimitUntil(null);
                localStorage.removeItem('password_reset_rate_limit');
            }
        };

        updateTimer(); // Initial update
        const interval = setInterval(updateTimer, 1000);

        return () => clearInterval(interval);
    }, [rateLimitUntil]);

    // Format time remaining as "MM:SS"
    const formatTimeRemaining = (ms: number): string => {
        const totalSeconds = Math.ceil(ms / 1000);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return `${ minutes }:${ seconds.toString().padStart(2, '0') }`;
    };

    // Validate token when detected in URL
    useEffect(() => {
        const token = searchParams.get('token');
        if (token) {
            validateResetToken(token);
        }
    }, [searchParams]);

    const validateResetToken = async (token: string) => {
        setIsValidatingToken(true);
        setTokenError(null);

        try {
            const response = await AuthService.call(`/auth/token/verify/${ token }`, 'GET');

            if (response.status === 200) {
                setResetToken(token);
                setStep(3); // Valid token, proceed to password reset
            } else {
                throw new Error(response.error?.detail || 'Invalid or expired reset link');
            }
        } catch (error: any) {
            logger.error('Token validation error:', error);
            setTokenError(error.message || 'This password reset link is invalid or has expired.');
            notify({
                title: 'Invalid Reset Link',
                message: error.message || 'This link is invalid or has expired. Please request a new one.',
                type: 'error',
                duration: 5000,
            });
        } finally {
            setIsValidatingToken(false);
        }
    };

    // Form for Step 1
    const emailForm = useForm<EmailFormData>({
        resolver: zodResolver(emailSchema),
        mode: 'onChange',
    });



    // Form for Step 3
    const passwordForm = useForm<PasswordFormData>({
        resolver: zodResolver(passwordSchema),
        mode: 'onChange',
    });

    const newPassword = passwordForm.watch('newPassword');

    // Password strength calculation (reusing logic from PasswordChange)
    const getPasswordStrength = (password: string) => {
        if (!password) return { score: 0, strength: 'weak' as const };

        let score = 0;
        if (password.length >= 8) score += 1;
        if (password.length >= 12) score += 1;
        if (password.length >= 16) score += 1;
        if (/[a-z]/.test(password)) score += 1;
        if (/[A-Z]/.test(password)) score += 1;
        if (/[0-9]/.test(password)) score += 1;
        if (/[^a-zA-Z0-9]/.test(password)) score += 1;

        let strength: 'weak' | 'fair' | 'good' | 'strong';
        if (score <= 3) strength = 'weak';
        else if (score <= 5) strength = 'fair';
        else if (score <= 6) strength = 'good';
        else strength = 'strong';

        return { score, strength };
    };

    const passwordStrength = getPasswordStrength(newPassword || '');

    const getStrengthColor = () => {
        switch (passwordStrength.strength) {
            case 'weak': return 'bg-red-500';
            case 'fair': return 'bg-orange-500';
            case 'good': return 'bg-yellow-500';
            case 'strong': return 'bg-green-500';
            default: return 'bg-gray-300';
        }
    };

    const getStrengthWidth = () => `${ (passwordStrength.score / 7) * 100 }%`;

    if (authLoading || isValidatingToken) {
        return <Spinner message={isValidatingToken ? "Verifying reset link..." : "Loading..."} />;
    }

    // If token validation failed, show error state
    if (tokenError) {
        return (
            <AuthLayout title={ETitle.RESET} tagLine={<p className="text-neutral-500 mt-2">Invalid or expired reset link</p>} callback={() => { }} hideOtherLogins>
                <div className="mt-8 space-y-6">
                    <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-lg">
                        <p className="text-sm text-destructive font-medium">
                            {tokenError}
                        </p>
                    </div>

                    <div className="space-y-3">
                        <p className="text-sm text-muted-foreground text-center">
                            The password reset link you clicked is either invalid or has expired. Links are valid for 5 minutes.
                        </p>

                        <ActionButton
                            loading={false}
                            textOnsubmit=""
                            btnText="Request New Reset Link"
                            click={() => {
                                setTokenError(null);
                                setStep(1);
                            }}
                        />
                    </div>

                    <div className="text-center pt-4 border-t border-border/40">
                        <Link to="/login" className="text-sm font-bold text-primary hover:opacity-80 transition-opacity">
                            Back to Login
                        </Link>
                    </div>
                </div>
            </AuthLayout>
        );
    }

    // Step 1: Request reset email
    const handleEmailSubmit = async (data: EmailFormData) => {
        setIsSubmitting(true);

        try {
            const response = await AuthService.call('/auth/forgot-password', 'POST', { email: data.email });

            if (response.status === HTTPStatus.OK) {
                setEmail(data.email);
                setStep(2);
                notify({
                    title: 'Reset Link Sent',
                    message: 'If an account exists with this email, you will receive a password reset link. Please check your inbox.',
                    type: 'success',
                    duration: 5000,
                });
            } else if (response.status === HTTPStatus.TOO_MANY_REQUESTS) {
                // Rate limit exceeded - set timestamp for 15 minutes from now
                const rateLimitTime = Date.now() + (15 * 60 * 1000); // 15 minutes
                localStorage.setItem('password_reset_rate_limit', rateLimitTime.toString());
                setRateLimitUntil(rateLimitTime);

                const retryAfter = response.error?.detail?.retry_after || '15 minutes';
                throw new Error(`Too many reset attempts. Please try again after ${ retryAfter }.`);
            } else {
                throw new Error(response.error?.message || 'Failed to send reset email');
            }
        } catch (error: any) {
            logger.error('Forgot password error:', error);
            notify({
                title: error.message?.includes('Too many') ? 'Rate Limit Exceeded' : 'Request Failed',
                message: error.message || 'Unable to process your request. Please try again.',
                type: 'error',
                duration: 7000,
            });
        } finally {
            setIsSubmitting(false);
        }
    };



    // Step 2/3: Reset password (Step 2 is just waiting for link, Step 3 is password reset)
    const handlePasswordSubmit = async (data: PasswordFormData) => {
        setIsSubmitting(true);

        try {
            // If we have a reset token from URL, use it; otherwise we need email
            const payload: any = resetToken
                ? { token: resetToken, password: data.newPassword }
                : { email: email, password: data.newPassword };

            const response = await AuthService.call('/auth/reset-password', 'POST', payload);

            if (response.status === 200) {
                notify({
                    title: 'Password Reset Successful',
                    message: 'Your password has been updated. You can now login with your new password.',
                    type: 'success',
                    duration: 5000,
                });

                // Redirect to login after a short delay
                setTimeout(() => {
                    navigate('/login', { replace: true });
                }, 2000);
            } else {
                throw new Error(response.error?.message || 'Failed to reset password');
            }
        } catch (error: any) {
            logger.error('Password reset error:', error);
            notify({
                title: 'Reset Failed',
                message: error.message || 'Unable to reset your password. Please try again.',
                type: 'error',
                duration: 5000,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderStepContent = () => {
        switch (step) {
            case 1:
                return (
                    <form onSubmit={emailForm.handleSubmit(handleEmailSubmit)} className="space-y-6">
                        <div className="space-y-2">
                            <Label htmlFor="email" className="text-foreground font-semibold uppercase tracking-widest text-[10px]">
                                Registered Email
                            </Label>
                            <Input
                                id="email"
                                type="email"
                                {...emailForm.register("email")}
                                placeholder="Enter your registered email"
                                className={input_style}
                                disabled={isSubmitting}
                            />
                            {emailForm.formState.errors.email && (
                                <p className="text-xs text-destructive mt-1">
                                    {emailForm.formState.errors.email.message}
                                </p>
                            )}
                        </div>

                        {/* Rate Limit Warning */}
                        {rateLimitUntil && timeRemaining > 0 && (
                            <div className="p-3 bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-900/30 rounded-lg">
                                <p className="text-sm text-orange-800 dark:text-orange-200 font-medium">
                                    Too many attempts. Please wait {formatTimeRemaining(timeRemaining)} before trying again.
                                </p>
                            </div>
                        )}

                        <ActionButton
                            loading={isSubmitting}
                            textOnsubmit="Sending..."
                            btnText={rateLimitUntil && timeRemaining > 0
                                ? `Wait ${ formatTimeRemaining(timeRemaining) }`
                                : "Send Reset Link"
                            }
                            click={() => { }}
                            disabled={!!(rateLimitUntil && timeRemaining > 0)}
                        />

                        <div className="text-center pt-4 border-t border-border/40">
                            <Link to="/login" className="text-sm font-bold text-primary hover:opacity-80 transition-opacity">
                                Back to Login
                            </Link>
                        </div>
                    </form>
                );

            case 2:
                return (
                    <div className="space-y-6">
                        <div className="p-4 bg-muted/50 border border-border/50 rounded-lg text-center">
                            <p className="text-sm text-muted-foreground">
                                We've sent a verification link to <span className="font-semibold text-foreground">{email}</span>
                            </p>
                            <p className="text-xs text-muted-foreground mt-2">
                                Please check your inbox and click the link to reset your password.
                            </p>
                        </div>

                        <div className="text-center pt-4 border-t border-border/40 space-y-2">
                            <button
                                type="button"
                                onClick={() => setStep(1)}
                                className="text-sm font-bold text-primary hover:opacity-80 transition-opacity block w-full"
                            >
                                Use Different Email
                            </button>
                            <Link to="/login" className="text-sm font-bold text-muted-foreground hover:text-primary transition-colors block">
                                Back to Login
                            </Link>
                        </div>
                    </div>
                );

            case 3:
                return (
                    <form onSubmit={passwordForm.handleSubmit(handlePasswordSubmit)} className="space-y-6">
                        {/* New Password */}
                        <div className="space-y-2">
                            <Label htmlFor="newPassword" className="text-foreground font-semibold uppercase tracking-widest text-[10px]">
                                New Password
                            </Label>
                            <div className="relative">
                                <Input
                                    id="newPassword"
                                    type={showPassword ? 'text' : 'password'}
                                    {...passwordForm.register("newPassword")}
                                    placeholder="Create a strong password"
                                    className={`${ input_style } pr-12`}
                                    disabled={isSubmitting}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                                >
                                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                </button>
                            </div>
                            {passwordForm.formState.errors.newPassword && (
                                <p className="text-xs text-destructive mt-1">
                                    {passwordForm.formState.errors.newPassword.message}
                                </p>
                            )}

                            {/* Password Strength Indicator */}
                            {newPassword && (
                                <div className="mt-3 space-y-2">
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-muted-foreground text-xs">Password strength:</span>
                                        <span className={`font-medium capitalize text-xs ${ passwordStrength.strength === 'weak' ? 'text-red-600' :
                                            passwordStrength.strength === 'fair' ? 'text-orange-600' :
                                                passwordStrength.strength === 'good' ? 'text-yellow-600' :
                                                    'text-green-600'
                                            }`}>
                                            {passwordStrength.strength}
                                        </span>
                                    </div>
                                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                                        <div
                                            className={`h-full transition-all duration-300 ${ getStrengthColor() }`}
                                            style={{ width: getStrengthWidth() }}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Confirm Password */}
                        <div className="space-y-2">
                            <Label htmlFor="confirmPassword" className="text-foreground font-semibold uppercase tracking-widest text-[10px]">
                                Confirm Password
                            </Label>
                            <div className="relative">
                                <Input
                                    id="confirmPassword"
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    {...passwordForm.register("confirmPassword")}
                                    placeholder="Confirm your password"
                                    className={`${ input_style } pr-12`}
                                    disabled={isSubmitting}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                                >
                                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                </button>
                            </div>
                            {passwordForm.formState.errors.confirmPassword && (
                                <p className="text-xs text-destructive mt-1">
                                    {passwordForm.formState.errors.confirmPassword.message}
                                </p>
                            )}
                            {passwordForm.watch('confirmPassword') && newPassword === passwordForm.watch('confirmPassword') && (
                                <p className="text-xs text-green-600 flex items-center gap-1 mt-1">
                                    <Check className="w-4 h-4" />
                                    Passwords match
                                </p>
                            )}
                        </div>

                        <ActionButton
                            loading={isSubmitting}
                            textOnsubmit="Resetting..."
                            btnText="Reset Password"
                            click={() => { }}
                        />

                        <div className="text-center pt-4 border-t border-border/40">
                            <Link to="/login" className="text-sm font-bold text-primary hover:opacity-80 transition-opacity">
                                Back to Login
                            </Link>
                        </div>
                    </form>
                );

            default:
                return null;
        }
    };

    const getTitle = () => {
        switch (step) {
            case 1: return ETitle.RESET;
            case 2: return 'Verify Code' as ETitle;
            case 3: return 'Create New Password' as ETitle;
            default: return ETitle.RESET;
        }
    };

    const getTagline = () => {
        switch (step) {
            case 1:
                return <p className="text-neutral-500 mt-2">Enter your email to receive a password reset code</p>;
            case 2:
                return <p className="text-neutral-500 mt-2">Check your email for the verification code</p>;
            case 3:
                return <p className="text-neutral-500 mt-2">Choose a strong password for your account</p>;
            default:
                return null;
        }
    };

    return (
        <AuthLayout title={getTitle()} tagLine={getTagline()} callback={() => { }} hideOtherLogins>
            <div className="mt-8">
                {renderStepContent()}
            </div>
        </AuthLayout>
    );
};

export default ForgotPassword;
